// Tiny audio helper. Plays one of two short clips (chat.wav / notify.wav)
// in response to realtime frames. The clips live in /public/sound/ and
// are served as plain static assets from the SPA host.
//
// Mute preference is stored in localStorage and exposed via useSoundEnabled
// so the header toggle can flip it.
//
// Browsers block audio playback until the user has interacted with the
// page (autoplay policy). The first attempt may silently fail with a
// rejected promise — that's expected. We swallow the rejection; the
// next play after any user gesture will work.

import { useEffect, useState } from 'react';

const STORAGE_KEY = 'haket.sound.enabled';

function readStored(): boolean {
  if (typeof localStorage === 'undefined') return true;
  const v = localStorage.getItem(STORAGE_KEY);
  if (v === null) return true; // default: on
  return v === '1';
}

// Same-tab change signal (storage events only fire in OTHER tabs).
const SOUND_EVENT = 'haket.sound-changed';

/**
 * Imperatively set the stored sound preference and notify listeners in this
 * tab. Used to hydrate the local fast-path from the server's saved value on
 * login WITHOUT echoing back to the server. No-ops if unchanged.
 */
export function applyStoredSound(enabled: boolean): void {
  if (typeof localStorage === 'undefined') return;
  const next = enabled ? '1' : '0';
  if (localStorage.getItem(STORAGE_KEY) === next) return;
  try { localStorage.setItem(STORAGE_KEY, next); } catch { /* private mode */ }
  window.dispatchEvent(new Event(SOUND_EVENT));
}

/** The current stored sound preference (defaults to true when unset). */
export function getStoredSound(): boolean {
  return readStored();
}

// One HTMLAudioElement per sound, lazily created. Reusing the element
// means we can call play() repeatedly without leaking instances.
const elements: Record<string, HTMLAudioElement> = {};

function el(src: string): HTMLAudioElement {
  let e = elements[src];
  if (!e) {
    e = new Audio(src);
    e.preload = 'auto';
    e.volume = 0.4; // legacy was full volume — soften
    elements[src] = e;
  }
  return e;
}

export type SoundKind = 'chat' | 'notify';

const SRC: Record<SoundKind, string> = {
  chat: '/sound/chat.wav',
  notify: '/sound/notify.wav',
};

/**
 * Fire-and-forget play. No-op if sounds are muted in localStorage.
 * Swallows rejections (autoplay policy / network).
 */
export function playSound(kind: SoundKind): void {
  if (!readStored()) return;
  if (typeof document !== 'undefined' && document.hidden) return; // tab in background
  const e = el(SRC[kind]);
  try {
    // Rewind so consecutive frames replay from the start instead of being ignored.
    e.currentTime = 0;
    const p = e.play();
    if (p && typeof p.catch === 'function') p.catch(() => { /* autoplay block */ });
  } catch { /* ignore */ }
}

// React hook for the toggle UI. Returns the live value + a setter that
// persists. Subscribes to storage events so a change in another tab
// flips this tab too.
export function useSoundEnabled(): { enabled: boolean; setEnabled: (v: boolean) => void } {
  const [enabled, setEnabledState] = useState<boolean>(() => readStored());

  useEffect(() => {
    const reread = () => setEnabledState(readStored());
    const onStorage = (e: StorageEvent) => { if (e.key === STORAGE_KEY) reread(); };
    // SOUND_EVENT fires same-tab on a programmatic applyStoredSound (e.g. server
    // hydration on login); storage fires for other tabs.
    window.addEventListener(SOUND_EVENT, reread);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(SOUND_EVENT, reread);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const setEnabled = (v: boolean) => {
    applyStoredSound(v);   // persist + same-tab notify
    setEnabledState(v);
  };

  return { enabled, setEnabled };
}
